'use client';

// Admin's badge dialect — a skin over the shared @auctogon/ui Badge (BL-1712,
// ADR 030 — Design Canon `Badge` row). The structure (dot, pulse, icon, count,
// truncation, title fallback) lives in the shared core; this file binds admin's
// appearance and nothing else. The tone and dot maps stay exported here because
// seven admin files read them (status dropdowns, menus, the segmented control's
// rationale) — they are the dialect.
//
// History: the canonical status / label badge — one contract that replaced the four
// pre-existing ones (the `.ui-badge` CSS dialect — deleted in BL-1583, StatusBadge/LotStatusBadge,
// the hammer-adjustment scope badge (BL-2056 deleted it with the adjustments
// list) / MaxBidBadge, the settlement shell pill) plus the ~88 inline
// `rounded-full` spans. Fixed 24px height, sentence-case, one radius, one tone
// map, optional leading dot for in-flight states.
//
// Deliberately self-contained (Tailwind palette classes, NOT the `.s-v2`
// `--teal/--ink` CSS vars) so it renders identically inside the v2 auctions
// shell AND on classic (non-v2) routes via LotStatusBadge. That portability is
// what lets the rest of the app adopt it later without first threading the v2
// tokens app-wide.

import type { ReactNode } from 'react';
import {
  Badge as UiBadge,
  badgeShellClassName,
  type BadgeDialect,
  type BadgeProps as UiBadgeProps,
  type BadgeShellTone,
} from '@auctogon/ui/web';

import type { BadgeTone } from '@auctogon/ui/types';

import ActiveDot from './ActiveDot';

export type { BadgeShellTone };

// One semantic map: tone → { bg-100, text-700, border-200 }.
export const TONE_CLASSES: Record<BadgeShellTone, string> = {
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  info: 'bg-blue-100 text-blue-700 border-blue-200',
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger: 'bg-rose-100 text-rose-700 border-rose-200',
  brand: 'bg-teal-100 text-teal-800 border-teal-200',
  // BL-1565: the purple `revoked` tone — a terminal state a person imposed
  // (today: a withdrawn lot). Deliberately NOT rose: with a live pulsing dot on
  // the ladder one row up, the danger tone read as an error beside the sale that
  // is running correctly, and it is the same rose every validation message wears.
  revoked: 'bg-purple-100 text-purple-700 border-purple-200',
  // BL-2424: orange for a state that needs action short of `danger` (invoice Past due).
  //   orange-800 on orange-100 ............... 6.38:1  text ✓ (needs 4.5)
  attention: 'bg-orange-100 text-orange-800 border-orange-200',
  // BL-2424: a dead record (invoice Void) — a dashed grey outline on white, so it
  // reads neither as neutral's solid grey nor as revoked's purple.
  //   gray-600 on white ...................... 7.56:1  text ✓ (needs 4.5)
  //   gray-500 dashed edge on white .......... 4.83:1  non-text ✓ (needs 3.0)
  inert: 'bg-white text-gray-600 border-gray-500 border-dashed',
  custom: '',
};

// BL-2569: the solid alert count, a red circle with a white number, for a count that needs someone
// to act (the Storage & Sync menu entry's changes that failed or were refused). It rides the `custom`
// shell tone, so the shared tone vocabulary (`BadgeTone`) and its maps are unchanged; with `count`
// the shell's 20px minimum width keeps one digit a circle.
//   white on red-600 ..................... 4.83:1  text ✓ (needs 4.5)
// The number and the entry's accessible name ("2 changes need attention") are the signal; the red
// fill on the dark rail is decoration beside them (1.89:1 on --sidebar-bg, ADR 051 rule 4).
export const ALERT_COUNT_CLASSES = 'border-red-600 bg-red-600 text-white';

// The leading dot uses the tone's -500 shade so it reads as a status indicator
// against the -100 background, a step stronger than the -700 text.
//
// Exported so a control that renders a tone's colour WITHOUT the full pill —
// today the customer page's status `Select`s' per-option dropdown dots — stays
// in lockstep with the badge it sits beside, rather than hardcoding its own swatch.
export const DOT_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-gray-500',
  info: 'bg-blue-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
  brand: 'bg-teal-600',
  revoked: 'bg-purple-500',
  // orange-600 on orange-100 ............... 3.11:1  non-text ✓ (orange-500 is 2.45:1)
  attention: 'bg-orange-600',
  // A hollow ring, so it never matches neutral's solid gray-500 dot beside it in the
  // status menu. gray-500 edge on white ..... 4.83:1  non-text ✓
  inert: 'border border-gray-500 bg-white',
};

const ADMIN_PULSE_DOT: ReactNode = <ActiveDot />;

/** Admin's appearance: the fixed 24px bordered pill (BL-1580), its tone and dot maps, the emerald pulse. */
const ADMIN_BADGE_DIALECT: BadgeDialect = {
  base: 'inline-flex h-6 max-w-full items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full border px-2 align-middle text-caption font-medium',
  count: 'min-w-[20px] justify-center px-1.5 tabular-nums',
  tones: TONE_CLASSES,
  dots: DOT_CLASSES,
  pulseDot: ADMIN_PULSE_DOT,
};

/**
 * The Badge shell as a class string, for a control that must be the badge but
 * cannot be a span element — the status dropdowns' button triggers (BL-1580).
 *
 * @param opts.tone - semantic tone (default `neutral`).
 * @param opts.count - numeric count badge: `min-w` floor + centered digits.
 * @returns The base + tone classes `<Badge>` itself renders with.
 */
export function badgeClassName(opts: { tone?: BadgeShellTone; count?: boolean } = {}): string {
  return badgeShellClassName(ADMIN_BADGE_DIALECT, opts);
}

/** Props for {@link Badge}: the shared ones, with admin's dialect bound. */
export type BadgeProps = Omit<UiBadgeProps, 'dialect'>;

/**
 * The canonical admin badge — the shared core in admin's dialect.
 *
 * @param props - tone, children, and the optional dot / icon / count modifiers.
 * @returns An inline pill-shaped badge.
 */
export default function Badge(props: BadgeProps) {
  return <UiBadge {...props} dialect={ADMIN_BADGE_DIALECT} />;
}
